import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiRequestError } from '../client.ts';
import type { MeResponse } from '../bindings/MeResponse.ts';
import type { LoginRequest } from '../bindings/LoginRequest.ts';
import type { RegisterRequest } from '../bindings/RegisterRequest.ts';
import type { ResetPasswordRequest } from '../bindings/ResetPasswordRequest.ts';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/v1/auth/me'),
    retry: (failureCount, error) =>
      !(error instanceof ApiRequestError && error.status === 401) && failureCount < 2,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: LoginRequest) =>
      apiFetch<MeResponse>('/v1/auth/login', { method: 'POST', body: JSON.stringify(req) }),
    onSuccess: (me) => queryClient.setQueryData(['me'], me),
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (req: RegisterRequest) =>
      apiFetch<MeResponse>('/v1/auth/register', { method: 'POST', body: JSON.stringify(req) }),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>('/v1/auth/logout', { method: 'POST' }),
    onSuccess: () => queryClient.removeQueries({ queryKey: ['me'] }),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<void>('/v1/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) }),
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<void>('/v1/auth/request-password-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (req: ResetPasswordRequest) =>
      apiFetch<void>('/v1/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<void>('/v1/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
  });
}
